import React from 'react';
import { CardElement, injectStripe, ReactStripeElements } from 'react-stripe-elements';
import throttle from 'lodash/throttle';

import Button from 'components/ButtonDeprecated';
import Label from 'components/Label';
import BoxBody from 'components/BoxBody';
import Text from 'components/Text';
import Box from 'components/Box';
import SidebarFooter from 'components/SidebarFooter';
import EventTracking from 'lib/EventTracking';
import errorLogger from 'utils/errorLogger';

import ToastActions from 'actions/ToastActions';
import { setUserStripeSource } from 'coaching/actions/CoachingActions';

import { CoachingBillingCard } from 'coaching/components/CoachingBillingView/types';

import 'coaching/components/styles/CoachingBillingStripe.scss';

import { coachingBillingViewPrefix } from 'coaching/components/CoachingBillingView/CoachingBillingView';

type CoachingBilligStripeProps = {
  onCloseSideBar: () => void;
};

type State = {
  cardError?: string;
  cardInputCompleted: boolean;
  cardFailureReason: string | null | undefined;
  submittingPayment: boolean;
};

class CoachingBillingStripe extends React.Component<
  CoachingBillingCard & CoachingBilligStripeProps & ReactStripeElements.InjectedStripeProps,
  State
> {
  onSubmitPayment: () => void;

  state: State = {
    cardError: '',
    cardInputCompleted: false,
    cardFailureReason: null,
    submittingPayment: false,
  };

  constructor(
    props: CoachingBillingCard & CoachingBilligStripeProps & ReactStripeElements.InjectedStripeProps
  ) {
    super(props);
    this.onSubmitPayment = throttle(this.submitPayment, 1000);
  }

  submitPayment = async () => {
    this.setState({ submittingPayment: true });
    const { source, error }: ReactStripeElements.SourceResponse = await this.props.stripe!.createSource();
    if (!source) {
      ToastActions.showErrorToast(
        error?.message ?? 'There was a problem saving your card. Please try again.'
      );
      console.warn(error?.message);
      if (error) {
        const ignoreErrorTypes = ['api_connection_error', 'api_error', 'card_error'];
        if (!ignoreErrorTypes.includes(error.type)) {
          errorLogger(`CoachingBillingStripe - CoachingStripe: There was an error creating the source. Error: ${error.type} - ${error.message}`)({
            code: error?.code,
            message: error?.message,
            err: error
          });
        }
      }
      this.setState({ cardFailureReason: error?.message });
      return;
    }
    await setUserStripeSource(source.id);
    EventTracking.trackKey(`${coachingBillingViewPrefix}.coaching_billing_stripe.updated_confirmation.view`);
    this.props.onCloseSideBar();
  }

  setCardInputCompleted(cardInputCompleted: boolean) {
    this.setState({ cardInputCompleted });
  }

  setCardFailureReason(cardFailureReason?: string | null) {
    this.setState({ cardFailureReason: cardFailureReason || null });
  }

  render() {
    return (
      <React.Fragment>
        <Box noShadow noBorder className="CoachingBillingStripe-container">
          <BoxBody size="xsmall">
            <Label htmlFor="cardnumber">Credit or debit card</Label>
            <CardElement
              data-test="credit-card-input"
              className={`CoachingBillingStripe-cardInput ${this.state.cardFailureReason ? 'CoachingBillingStripe-cardInput--error' : ''
                }`}
              onChange={(e: stripe.elements.ElementChangeResponse) => {
                if (e.complete && !e.error) {
                  this.setCardFailureReason(null);
                  this.setCardInputCompleted(true);
                } else {
                  this.setCardInputCompleted(false);
                  this.setCardFailureReason(e.error?.message);
                }
                if (!e.error) {
                  EventTracking.trackKey(`${coachingBillingViewPrefix}.coaching_billing_stripe.card_number.click`);
                }
              }}
            />
          </BoxBody>
        </Box>
        <SidebarFooter>
          <BoxBody size="small">
            {this.state.cardInputCompleted && (
              <Text data-test="coaching-payment-note" className="CoachingBillingStripe--note" align="center">
                Your card will be charged the day of your next session
              </Text>
            )}
            <Button
              full
              data-test="update-payment-button"
              color="mascot"
              disabled={!this.state.cardInputCompleted}
              onClick={this.onSubmitPayment}
              loading={this.state.submittingPayment}
              level="primary"
              eventKey={`${coachingBillingViewPrefix}.coaching_billing_stripe.button.click`}>
              Update card
            </Button>
          </BoxBody>
        </SidebarFooter>
      </React.Fragment>
    );
  }
}
export { CoachingBillingStripe };
export default injectStripe(CoachingBillingStripe);
